import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import ResourceForm from "@/components/ResourceForm";
import VideosManager from "@/components/VideosManager";
import { ReviewImportButton, ReviewImportsTab, KbImportButton } from "@/components/ReviewImport";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, Settings as SettingsIcon, Users as UsersIcon, BarChart3, AlertTriangle, Download } from "lucide-react";
import { CLASSIFICATIONS } from "@/components/Classification";
import { useBranding } from "@/context/BrandingContext";

const STATUS = ["Draft", "Approved", "Retired"];
const YN = ["Yes", "No"];
const CLS = { name: "classification", label: "Content Classification", type: "select", options: CLASSIFICATIONS };

const CONFIGS = {
  modules: {
    label: "Training Modules", titleField: "title",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "order", label: "Order", type: "number" },
      { name: "description", label: "Short description", type: "text" },
      { name: "explanation", label: "Explanation", type: "textarea" },
      { name: "image_url", label: "Image URL", type: "text" },
      { name: "video_url", label: "Video URL", type: "text" },
      { name: "common_mistakes", label: "Common mistakes (one per line)", type: "list" },
      { name: "pass_mark", label: "Pass mark %", type: "number" },
      { name: "status", label: "Status", type: "select", options: STATUS },
      { name: "version", label: "Version", type: "text" },
      { name: "last_reviewed", label: "Last reviewed (YYYY-MM-DD)", type: "text" },
      { name: "quiz", label: "Quiz (JSON array of {question, options[], correct_index})", type: "json", default: "[]" },
    ],
  },
  articles: {
    label: "Troubleshooting Articles", titleField: "problem", review: true,
    fields: [
      CLS,
      { name: "product_category", label: "Product Category", type: "text" },
      { name: "product_family", label: "Product Family", type: "text" },
      { name: "product_name", label: "Product Name", type: "text" },
      { name: "model", label: "Model", type: "text" },
      { name: "sku", label: "SKU", type: "text" },
      { name: "problem", label: "Problem / Complaint", type: "text" },
      { name: "symptoms", label: "Symptoms", type: "textarea" },
      { name: "possible_cause", label: "Possible Cause", type: "textarea" },
      { name: "before_you_start", label: "Before You Start / Initial Checks", type: "textarea" },
      { name: "safety_warning", label: "Safety Warning", type: "textarea" },
      { name: "steps", label: "Step-by-Step (one per line)", type: "list" },
      { name: "video_url", label: "Approved Video URL", type: "text" },
      { name: "video_customer_safe", label: "Video customer-safe?", type: "select", options: YN },
      { name: "spare_part", label: "Required Spare Part", type: "text" },
      { name: "spare_part_code", label: "Spare Part Code", type: "text" },
      { name: "resolution", label: "Resolution", type: "textarea" },
      { name: "tell_customer", label: "What to Tell Customer", type: "textarea" },
      { name: "escalation_point", label: "Escalation Point", type: "text" },
      { name: "approval_required", label: "Approval Required?", type: "text" },
      { name: "technician_required", label: "Technician Required?", type: "select", options: YN },
      { name: "source", label: "Source", type: "text" },
      { name: "last_reviewed", label: "Last Reviewed", type: "text" },
      { name: "version", label: "Version", type: "text" },
      { name: "status", label: "Status", type: "select", options: STATUS },
    ],
  },
  techprocedures: {
    label: "Technician Procedures", titleField: "title",
    fields: [
      { name: "order", label: "Order", type: "number" },
      { name: "title", label: "Title", type: "text" },
      CLS,
      { name: "warning", label: "Warning banner", type: "textarea" },
      { name: "steps", label: "Steps (one per line)", type: "list" },
      { name: "source", label: "Source", type: "text" },
      { name: "last_reviewed", label: "Last Reviewed", type: "text" },
      { name: "version", label: "Version", type: "text" },
      { name: "status", label: "Status", type: "select", options: STATUS },
    ],
  },
  videos: {
    label: "Videos", titleField: "title", review: true,
    fields: [
      { name: "title", label: "Video Title", type: "text" },
      { name: "video_url", label: "Video URL (YouTube/Vimeo/iframe) — leave blank until approved link is ready", type: "text" },
      CLS,
      { name: "customer_safe", label: "Customer Safe? (must be Yes for staff to send to customers)", type: "select", options: YN },
      { name: "product", label: "Product", type: "text" },
      { name: "product_family", label: "Product Family", type: "text" },
      { name: "model", label: "Model", type: "text" },
      { name: "sku", label: "SKU", type: "text" },
      { name: "problem", label: "Problem / Symptom", type: "text" },
      { name: "procedure", label: "Troubleshooting Procedure", type: "text" },
      { name: "step", label: "Exact Troubleshooting Step", type: "text" },
      { name: "linked_articles", label: "Linked Article IDs (comma separated)", type: "text" },
      { name: "description", label: "Caption / Description", type: "textarea" },
      { name: "safety_warning", label: "Safety Warning", type: "textarea" },
      { name: "escalation_point", label: "Escalation Instructions", type: "text" },
      { name: "source", label: "Source", type: "text" },
      { name: "last_reviewed", label: "Review Date", type: "text" },
      { name: "version", label: "Version", type: "text" },
      { name: "active", label: "Active / Retired", type: "select", options: ["Draft", "Active", "Retired"] },
      { name: "status", label: "Status", type: "select", options: STATUS },
    ],
  },
  spareparts: {
    label: "Spare Parts", titleField: "part_name",
    fields: [
      { name: "product", label: "Product", type: "text" },
      { name: "model", label: "Model", type: "text" },
      { name: "part_name", label: "Part Name", type: "text" },
      { name: "part_code", label: "Part Code", type: "text" },
      { name: "photo", label: "Photo URL", type: "text" },
      { name: "compatible_models", label: "Compatible Models", type: "text" },
      { name: "troubleshooting_required", label: "Troubleshooting required?", type: "select", options: YN },
      { name: "approval_required", label: "Approval required?", type: "select", options: YN },
      { name: "install_instructions", label: "Installation Instructions", type: "textarea" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  contacts: {
    label: "Service Contacts", titleField: "state",
    fields: [
      { name: "state", label: "State", type: "text" },
      { name: "primary_name", label: "Primary contact / Arrange First", type: "text" },
      { name: "primary_role", label: "Primary role", type: "text" },
      { name: "primary_email", label: "Primary email", type: "text" },
      { name: "alt_name", label: "Alternative contact", type: "text" },
      { name: "alt_role", label: "Alternative role", type: "text" },
      { name: "alt_email", label: "Alternative email", type: "text" },
      { name: "phone", label: "Phone (optional)", type: "text" },
      { name: "service_area", label: "Service area (optional)", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
      { name: "active", label: "Active / Inactive", type: "select", options: ["Active", "Inactive"] },
      { name: "order", label: "Display order", type: "number" },
      { name: "last_reviewed", label: "Last reviewed", type: "text" },
    ],
  },
  templates: {
    label: "Customer Templates", titleField: "title",
    fields: [
      { name: "category", label: "Category", type: "text" },
      { name: "title", label: "Title", type: "text" },
      CLS,
      { name: "subject", label: "Subject", type: "text" },
      { name: "body", label: "Body", type: "textarea" },
    ],
  },
  safety: {
    label: "Safety Rules", titleField: "title",
    fields: [
      { name: "order", label: "Order", type: "number" },
      { name: "title", label: "Title", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
    ],
  },
  approvals: {
    label: "Approval Rules", titleField: "outcome",
    fields: [
      { name: "outcome", label: "Warranty Outcome", type: "text" },
      { name: "approver", label: "Who Approves", type: "text" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  notices: {
    label: "Warranty Notices", titleField: "title",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "body", label: "Body", type: "textarea" },
      { name: "date", label: "Date", type: "text" },
    ],
  },
  flows: {
    label: "Guided Flows", titleField: "title",
    fields: [
      { name: "title", label: "Title", type: "text" },
      { name: "category", label: "Category", type: "text" },
      CLS,
      { name: "description", label: "Description", type: "textarea" },
      { name: "model_warning", label: "Model warning banner", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS },
      { name: "start", label: "Start node id", type: "text" },
      { name: "nodes", label: "Nodes (JSON object)", type: "json", default: "{}" },
    ],
  },
};

function ResourceManager({ collection }) {
  const cfg = CONFIGS[collection];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get(`/content/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [collection]);

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/content/${collection}/${editing.id}`, data);
      else await api.post(`/content/${collection}`, data);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/content/${collection}/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-slate-900">{cfg.label} <span className="text-slate-400 font-normal">({items.length})</span></h3>
        <Button className="bg-mlily-blue hover:bg-mlily-bluehover gap-1" onClick={() => { setEditing({}); setOpen(true); }} data-testid={`add-${collection}-btn`}>
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>
      <div className="grid gap-2">
        {items.map((it) => (
          <Card key={it.id} className="p-3 flex items-center justify-between gap-3" data-testid={`admin-item-${it.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-900 truncate">{it[cfg.titleField] || it.id}</div>
              <div className="text-xs text-slate-400 flex gap-2 items-center flex-wrap">
                {it.status && <Badge variant="outline" className="text-[10px]">{it.status}</Badge>}
                {it.model && <span>{it.model}</span>}
                {it.updated_at && <span>updated {it.updated_at.slice(0, 10)}</span>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => { setEditing(it); setOpen(true); }} data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDelId(it.id)} data-testid={`delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-400">No items yet.</p>}
      </div>

      {open && (
        <ResourceForm open={open} onOpenChange={setOpen} fields={cfg.fields} initial={editing}
          onSave={save} title={editing?.id ? `Edit ${cfg.label}` : `New ${cfg.label}`} />
      )}

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={del} data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UsersManager() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ email: "", name: "", password: "", role: "staff" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/users/${editing.id}`, { name: form.name, role: form.role, password: form.password || undefined });
      else await api.post("/users", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/users/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-slate-900">Users ({users.length})</h3>
        <Button className="bg-mlily-blue hover:bg-mlily-bluehover gap-1" onClick={openNew} data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add user</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge className={u.role === "admin" ? "bg-mlily-blue" : "bg-emerald-500"}>{u.role}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDelId(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div className="space-y-1.5"><Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">staff</SelectItem><SelectItem value="admin">admin</SelectItem></SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-mlily-blue hover:bg-mlily-bluehover" onClick={save} data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={del} data-testid="confirm-delete-user-btn">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BrandingManager() {
  const { refresh } = useBranding();
  const [b, setB] = useState({ title: "", subtitle: "", logo_url: null, favicon_url: null });
  const [busy, setBusy] = useState(false);
  const base = API.replace(/\/api$/, "");

  const load = () => api.get("/branding").then((r) => setB(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveText = async () => {
    try { await api.put("/branding", { title: b.title, subtitle: b.subtitle }); toast.success("Branding saved"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (e, kind) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/branding/${kind}`, fd);
      setB(data); toast.success(`${kind === "logo" ? "Logo" : "Favicon"} updated`); refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const logoSrc = b.logo_url ? `${base}${b.logo_url}` : "/mlily-logo.png";
  const favSrc = b.favicon_url ? `${base}${b.favicon_url}` : "/favicon.png";

  return (
    <Card className="p-6 space-y-5 max-w-xl" data-testid="branding-manager">
      <h3 className="font-heading font-bold text-slate-900">Branding</h3>
      <p className="text-xs text-slate-500 -mt-3">Upload a new logo or favicon and it updates across the whole portal automatically.</p>

      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
          <img src={logoSrc} alt="Logo preview" className="w-full h-full object-contain" data-testid="branding-logo-preview" />
        </div>
        <div>
          <Label>Main logo</Label>
          <Input type="file" accept="image/*" disabled={busy} onChange={(e) => upload(e, "logo")} data-testid="branding-logo-input" className="mt-1" />
          <p className="text-xs text-slate-400 mt-1">PNG/JPG/WebP/SVG. Max 3 MB. Not stretched or cropped.</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
          <img src={favSrc} alt="Favicon preview" className="w-full h-full object-contain" data-testid="branding-favicon-preview" />
        </div>
        <div>
          <Label>Favicon (browser tab)</Label>
          <Input type="file" accept="image/*" disabled={busy} onChange={(e) => upload(e, "favicon")} data-testid="branding-favicon-input" className="mt-1" />
          <p className="text-xs text-slate-400 mt-1">Square image recommended. Max 3 MB.</p>
        </div>
      </div>

      <div className="space-y-1.5"><Label>Portal title</Label>
        <Input value={b.title || ""} onChange={(e) => setB({ ...b, title: e.target.value })} data-testid="branding-title-input" /></div>
      <div className="space-y-1.5"><Label>Portal subtitle</Label>
        <Input value={b.subtitle || ""} onChange={(e) => setB({ ...b, subtitle: e.target.value })} data-testid="branding-subtitle-input" /></div>
      <Button className="bg-mlily-blue hover:bg-mlily-bluehover" onClick={saveText} data-testid="save-branding-btn">Save title & subtitle</Button>
    </Card>
  );
}

function SettingsManager() {
  const [s, setS] = useState({ warranty_tracker_url: "", warranty_claim_form_url: "", review_period_days: 180 });
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-6 space-y-4 max-w-xl">
      <h3 className="font-heading font-bold text-slate-900">Portal Settings</h3>
      <div className="space-y-1.5"><Label>Live Warranty Tracker URL (WorkDrive)</Label>
        <Input value={s.warranty_tracker_url || ""} onChange={(e) => setS({ ...s, warranty_tracker_url: e.target.value })} data-testid="settings-tracker-url" /></div>
      <div className="space-y-1.5"><Label>Warranty Claim Form URL</Label>
        <Input value={s.warranty_claim_form_url || ""} onChange={(e) => setS({ ...s, warranty_claim_form_url: e.target.value })} data-testid="settings-claim-url" /></div>
      <div className="space-y-1.5"><Label>Content review reminder period (days)</Label>
        <Input type="number" value={s.review_period_days || 180} onChange={(e) => setS({ ...s, review_period_days: Number(e.target.value) })} data-testid="settings-review-days" /></div>
      <Button className="bg-mlily-blue hover:bg-mlily-bluehover" onClick={save} data-testid="save-settings-btn">Save settings</Button>
    </Card>
  );
}

function ProgressView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/progress").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card>
      <Table>
        <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Module</TableHead><TableHead>Score</TableHead><TableHead>Result</TableHead><TableHead>Completed</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} data-testid={`progress-row-${i}`}>
              <TableCell className="font-medium">{r.user_name}<div className="text-xs text-slate-400">{r.user_email}</div></TableCell>
              <TableCell>{r.module_title}</TableCell>
              <TableCell>{r.score}%</TableCell>
              <TableCell><Badge className={r.passed ? "bg-emerald-500" : "bg-red-500"}>{r.passed ? "Passed" : "Failed"}</Badge></TableCell>
              <TableCell className="text-sm text-slate-500">{(r.completed_at || "").slice(0, 10)}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-slate-400 text-center py-6">No quiz results yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function ReviewReminder() {
  const [stale, setStale] = useState([]);
  useEffect(() => {
    (async () => {
      const [set, arts, vids] = await Promise.all([
        api.get("/settings"), api.get("/content/articles"), api.get("/content/videos"),
      ]);
      const days = set.data.review_period_days || 180;
      const cutoff = new Date(Date.now() - days * 86400000);
      const check = (list, kind) => list.filter((x) => {
        const d = x.last_reviewed ? new Date(x.last_reviewed) : null;
        return d && d < cutoff;
      }).map((x) => ({ kind, title: x.problem || x.title, date: x.last_reviewed }));
      setStale([...check(arts.data, "Article"), ...check(vids.data, "Video")]);
    })().catch(() => {});
  }, []);
  if (stale.length === 0) return null;
  return (
    <Card className="p-4 border-amber-300 bg-amber-50" data-testid="review-reminder">
      <div className="flex items-center gap-2 text-amber-700 font-semibold"><AlertTriangle className="w-5 h-5" /> {stale.length} item(s) due for review</div>
      <ul className="mt-2 text-sm text-amber-800 space-y-1">
        {stale.map((s, i) => <li key={i}>• {s.kind}: {s.title} (last reviewed {s.date})</li>)}
      </ul>
    </Card>
  );
}

function DraftExportButton() {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/draft-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "MLILY_Draft_Troubleshooting_Review.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Draft review spreadsheet downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Export failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button variant="outline" onClick={download} disabled={loading}
      className="gap-2 border-mlily-blue/40 text-mlily-blue hover:bg-mlily-blue hover:text-white"
      data-testid="download-draft-export-btn">
      <Download className="w-4 h-4" /> {loading ? "Preparing…" : "Export Drafts (Excel)"}
    </Button>
  );
}

export default function Admin() {
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-mlily-blue">Admin Area</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Manage Portal</h1>
        </div>
        <div className="flex items-center gap-3">
          <DraftExportButton />
          <KbImportButton onApplied={() => window.location.reload()} />
          <ReviewImportButton onApplied={() => window.location.reload()} />
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-mlily-blue" data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </div>

      <ReviewReminder />

      <Tabs defaultValue="modules">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="techprocedures" data-testid="tab-techprocedures">Technician</TabsTrigger>
          <TabsTrigger value="flows" data-testid="tab-flows">Guided Flows</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
          <TabsTrigger value="spareparts" data-testid="tab-spareparts">Spare Parts</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">Approvals</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety">Safety</TabsTrigger>
          <TabsTrigger value="notices" data-testid="tab-notices">Notices</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-1" />Users</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress"><BarChart3 className="w-4 h-4 mr-1" />Progress</TabsTrigger>
          <TabsTrigger value="reviewimports" data-testid="tab-reviewimports">Review Imports</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="w-4 h-4 mr-1" />Settings</TabsTrigger>
        </TabsList>
        {Object.keys(CONFIGS).map((k) => (
          <TabsContent key={k} value={k} className="mt-5">
            {k === "videos" ? <VideosManager /> : <ResourceManager collection={k} />}
          </TabsContent>
        ))}
        <TabsContent value="users" className="mt-5"><UsersManager /></TabsContent>
        <TabsContent value="progress" className="mt-5"><ProgressView /></TabsContent>
        <TabsContent value="reviewimports" className="mt-5"><ReviewImportsTab /></TabsContent>
        <TabsContent value="settings" className="mt-5"><BrandingManager /><div className="mt-6"><SettingsManager /></div></TabsContent>
      </Tabs>
    </div>
  );
}
